import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useParams } from 'react-router-dom';
import NsContent from 'components/common/ns-content/NsContent';

import {
  selectDetails,
  selectErrors,
  selectIsLoading,
  setFormField,
  slice as emailQuoteSlice,
  setSuccess
} from 'state/email-quote/email-quote-slice';
import { selectProductByCode } from 'state/quote/quote-slice';
import { useValidateEmailThunk } from 'hooks/useEmailValidationThunk';
import { sendQuoteThunk } from 'state/email-quote/email-quote-thunks';
import { useEmailQuotePayload } from 'components/direct-sales/quote/hooks/useEmailQuotePayload';
import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { NsTitle } from 'components/common/ns-title/NsTitle';
import { NsBackCta } from 'components/common/ns-back-cta/NsBackCta';
import { NsEmailInput } from 'components/common/ns-email-input/NsEmailInput';
import { Error } from 'components/common/error/Error';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { useMaskPII } from 'hooks/useMaskPII';
import { NsForm } from 'components/common/ns-form/NsForm';
import { NsStopgap } from 'components/common/ns-stopgap/NsStopgap';
import { NsFirstNameInput } from 'components/common/ns-first-name-input/NsFirstNameInput';
import { NsLastNameInput } from 'components/common/ns-last-name-input/NsLastNameInput';
import { useNavigation } from 'hooks/useNavigation';

export const testId = 'emailQuoteTestId';

export const EmailQuote = () => {
  const dispatch = useDispatch();
  const { productCode } = useParams();
  const product = useSelector(selectProductByCode(productCode));
  const userDetails = useSelector(selectDetails, shallowEqual);
  const apiErrors = useSelector(selectErrors, shallowEqual);
  const isLoading = useSelector(selectIsLoading, shallowEqual);
  const validateEmailThunk = useValidateEmailThunk(emailQuoteSlice);
  const payload = useEmailQuotePayload(product);
  const { mask } = useMaskPII();
  const { next, eventTypes } = useNavigation();

  const { email, lastName, firstName } = userDetails;

  const handleChange = (event) => {
    const {
      target: { value, name }
    } = event;

    dispatch(setFormField({ name, value }));
  };

  const handleGoNext = async (e) => {
    e.preventDefault();

    try {
      await dispatch(validateEmailThunk(email));
      const response = await dispatch(sendQuoteThunk(payload));
      next({
        eventType: eventTypes.NEXT,
        wasVisited: true,
        callback: () => {
          dispatch(setSuccess(response));
          dispatch(setFormField({ name: 'email', value: email }));
        }
      });
    } catch (e) {
      e && console.error(e);
    }
  };

  if (isLoading) {
    return <NsStopgap />;
  }

  return (
    <NsPanel testId={testId}>
      <NsBackCta />
      <NsContent>
        <NsTitle title="Want to come back later?" />
        <p>
          We’ll send you an email with a link back here, so you can easily pick
          up where you left off. All you need to do is fill in the details
          below.
        </p>
      </NsContent>
      <NsForm onSubmit={handleGoNext}>
        <NsFirstNameInput
          value={firstName}
          onChange={handleChange}
          inputAttributes={mask}
        />
        <NsLastNameInput
          label="Surname"
          value={lastName}
          onChange={handleChange}
          inputAttributes={mask}
        />

        <NsEmailInput
          id="email"
          name="email"
          label="Email"
          value={email}
          onChange={handleChange}
          inputAttributes={mask}
        />
        <Error errors={apiErrors} />
        <NsPrimaryCta isLoading={isLoading}>Email me</NsPrimaryCta>
      </NsForm>
    </NsPanel>
  );
};

export default EmailQuote;
